import { Loader2, Save, Trash2 } from 'lucide-react';

const CATEGORIES = ['pnl', 'budget', 'cash_flow', 'ar_ap', 'consolidation', 'tax', 'payroll', 'inventory', 'custom'];

export function SkillEditor({ draft, onChange, onSave, onDelete, saving, isNew }) {
  const update = (field, value) => onChange({ ...draft, [field]: value });

  return (
    <div className="flex-1 flex flex-col border-r border-gray-800 overflow-hidden">
      {/* Metadata bar */}
      <div className="px-5 py-3 border-b border-gray-800 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] text-gray-500 uppercase tracking-wider mb-1">Name</label>
            <input
              value={draft.name}
              onChange={e => update('name', e.target.value)}
              placeholder="e.g., pnl_analysis"
              className="w-full bg-gray-800/60 border border-gray-700/50 rounded-lg px-3 py-1.5 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-violet-500/50"
            />
          </div>
          <div>
            <label className="block text-[11px] text-gray-500 uppercase tracking-wider mb-1">Category</label>
            <select
              value={draft.category || ''}
              onChange={e => update('category', e.target.value || null)}
              className="w-full bg-gray-800/60 border border-gray-700/50 rounded-lg px-3 py-1.5 text-sm text-gray-200 focus:outline-none focus:border-violet-500/50"
            >
              <option value="">None</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-[11px] text-gray-500 uppercase tracking-wider mb-1">Description (shown in agent prompt)</label>
          <input
            value={draft.description}
            onChange={e => update('description', e.target.value)}
            placeholder="1-2 sentence summary of what this skill provides"
            className="w-full bg-gray-800/60 border border-gray-700/50 rounded-lg px-3 py-1.5 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-violet-500/50"
          />
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.is_active}
              onChange={e => update('is_active', e.target.checked)}
              className="rounded border-gray-600 bg-gray-800 text-violet-500 focus:ring-violet-500/30"
            />
            Active
          </label>
          <span className="text-[11px] text-gray-600">
            {draft.is_active ? 'Agent will discover this skill' : 'Hidden from agent'}
          </span>
        </div>
      </div>

      {/* Content editor */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <label className="block text-[11px] text-gray-500 uppercase tracking-wider px-5 pt-3 pb-1">
          Content (Markdown — loaded when agent calls load_skill)
        </label>
        <textarea
          value={draft.content}
          onChange={e => update('content', e.target.value)}
          placeholder="# Skill Name&#10;&#10;## Account Types&#10;...&#10;&#10;## SQL Patterns&#10;```sql&#10;SELECT ...&#10;```&#10;&#10;## Business Rules&#10;- ..."
          className="flex-1 bg-gray-950 text-gray-200 text-sm font-mono px-5 py-3 resize-none focus:outline-none placeholder-gray-700 leading-relaxed"
          spellCheck={false}
        />
      </div>

      {/* Action bar */}
      <div className="flex items-center justify-between px-5 py-3 border-t border-gray-800">
        <div>
          {onDelete && (
            <button
              onClick={onDelete}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          )}
        </div>
        <button
          onClick={onSave}
          disabled={saving || !draft.name.trim() || !draft.content.trim()}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 disabled:bg-gray-700 disabled:text-gray-500 rounded-lg transition-colors"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {isNew ? 'Create' : 'Save'}
        </button>
      </div>
    </div>
  );
}
